import { CustomErrorCommit, type CommitError } from './types'
import { sql } from 'drizzle-orm'
import {
  TreeManager,
  TreeNode,
  getTreeManager,
} from '@basehub/utils/tree/index'
import { addToDependenciesToRevalidate } from '@basehub/data/replicache/helpers/gql'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import {
  Commit,
  commitBaseOps,
  isBlockWithChildren,
  Block,
  SelectBlock,
  ReferenceBlock,
  isReferenceableBlock,
  isRootBlock,
  parseSpaceName,
  isRichTextBlock,
  branchBaseOps,
  InstanceBlock,
  isComponentBlock,
  isInstanceBlock,
  isListBlock,
  Branch,
  ContentRequest,
  GenericNotification,
  hasVariantOverrides,
  getVariantsDataFromKey,
  isVariantSetBlock,
  BlockType,
  isWorkflowBlock,
  SvgValidator,
  isReferenceBlock,
  isEventBlock,
  BlockRelationship,
  brsByParentId,
  CommonBRsCompoundIndexes,
  blockRelationshipBaseOps,
  CommonBlocksCompoundIndexes,
  commonBlocksCompoundIndexes,
  getChildrenKeys,
  hashBr,
  BlockMilestone,
  blockMilestoneBaseOps,
  getBRId,
  isBRInRoot,
  BranchLineage,
  RootBlock,
  isBranch,
  isVirtualizedInstanceChildTargetBR,
  getOnChangeHandler,
} from '@basehub/data/replicache/index'
import { getId, isValidId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'
import {
  richTextExtensionsForTransformations,
  stateToJson,
  recursivelyProcessTipTapNodes,
  getIdFromBaseHubNode,
  tipTapJSONToHTMLString,
  isEmpty,
  enhanceGeneratedHTML,
  isEmptyJson,
  getInstancesInRichText,
} from '@basehub/tiptap-api-utils'
import { IndexedMap, createIndexedMap } from '@basehub/utils/indexed-map/index'
import { ServerOnlyParams, WithServerSecret } from '../../../tx/spec'
import { isServer, SYSTEM_AUTHORS } from '@basehub/shared-constants/index'
import { Entry, getResolvedRefGqlCacheId, schema } from '@basehub/data/db/index'
import { beginPerfReport } from '@basehub/utils/perf/index'
import {
  CheckUsageAllowsCommitParams,
  CustomBlocksUsageError,
  checkUsageAllowsCommit,
} from '@basehub/data/db/billing/blocks-usage'
import { WriteTransaction } from 'replicache'
import { serverAuthorizedOrThrow } from '../../../tx/server-auth'
import { convertToBytes, MaxSizeUnit } from '@basehub/utils/image-utils'
import {
  findAcceptedTypeOptionForBlock,
  getAcceptedTypeOptions,
} from '@basehub/data/replicache/schema/block/reference/util'
import {
  getAllowedFormatting,
  optionToNewOptions,
  richTextOptions,
  tiptapNodeToRichTextOption,
} from '@basehub/tiptap-api-utils/options'
import _uniq from 'lodash/uniq'
import { BlockWithBR, mergeBlocks } from './merge-helpers'
import { repoSpaceMutators } from '../mutators'
import cloneDeep from 'lodash/cloneDeep'
import { idToSourceBlockId } from '../block/mutators/copy-on-write'
import { getOriginUrl, resolveWebhookUrl } from './url-utils'
import { EventSubscriber } from '@basehub/data/replicache/schema/block/events/subscribers/schema'
import pMap from 'p-map'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import {
  getMilestoneId,
  flattenContributors,
} from '@basehub/data/replicache/schema/block-milestone/util'
import { isBefore, subMilliseconds } from 'date-fns'
import { proxyRichTextAssets } from '@basehub/utils/asset-proxy'
import isEqual from 'lodash/isEqual'
import {
  WebhookActionReady,
  WorkflowActionReady,
} from '@basehub/data/replicache/schema/block/workflow/inngest'
import { fastForward } from './fast-forward'
import {
  allowsAnyFile,
  getAllowedMimeTypesArray,
} from '@basehub/data/replicache/schema/block/valid-mime-types'

// this on true causes problems: naively, some blocks might be added twice to the new committed tree. should fix before re-enabling.
const FINE_GRAINED_PROCESSING = false

type UsageInfo = Pick<
  CheckUsageAllowsCommitParams,
  'currentTeamBlocksUsage' | 'explicitLimit' | 'maxPlanAllowance'
>

type CommitInput = Pick<Commit, 'id' | 'createdAt' | 'message' | 'authorId'> & {
  skipWorkflows?: boolean
  baseBranch: Branch
  source: {
    rootBlockId: string
  }
  head: {
    rootBlockId: string
    commitId: string
  } | null
  commonAncestor: {
    rootBlockId: string
    commitId: string
  } | null
  usageInfo: UsageInfo | null
  serverSecret: string
  stagedBlockIds: Block['id'][]
  testMode?: boolean
  debug?: boolean
} & (
    | { type?: 'commit' | undefined }
    | {
        type: 'merge'
        sourceBranch: Branch
        conflictResolutions: ContentRequest['conflictResolutions']
      }
  )

export type CommitMutationInput = WithServerSecret<CommitInput>

const DEBUG = true
const logger: typeof console.log = (...args) => {
  // eslint-disable-next-line no-console
  DEBUG && console.log('[COMMIT MUTATOR]', ...args)
}

const commit = async (
  tx: WriteTransaction,
  {
    id,
    createdAt,
    message,
    authorId,
    source,
    head,
    commonAncestor,
    baseBranch,
    usageInfo,
    stagedBlockIds,
    skipWorkflows,
    testMode,
    debug: _,
    ...rest
  }: CommitMutationInput
) => {
  console.log('[COMMIT MUTATOR] Starting commit', {
    commitId: id,
    branchId: baseBranch.id,
    branchName: baseBranch.name,
    sourceRootBlockId: source.rootBlockId,
    headCommitId: head?.commitId ?? null,
    headRootBlockId: head?.rootBlockId ?? null,
    commonAncestorCommitId: commonAncestor?.commitId ?? null,
    stagedBlockIdsCount: stagedBlockIds.length,
    stagedBlockIds,
    type: rest.type ?? 'commit',
    testMode,
  })

  ensureBaseHubTransaction(tx)

  if (!testMode) {
    serverAuthorizedOrThrow(tx.serverSecret)
  }
  invariant(isValidId(id), `Invalid commit id: ${id}`)
  invariant(tx.serverOnly, 'serverOnly should be true')
  invariant(isServer, 'commit() should only be called on the server')

  const perfReporter = beginPerfReport('commit', { logOnCheckpoint: true })

  function executor() {
    ensureBaseHubTransaction(tx)
    invariant(isServer, 'executor() should only be called on the server')
    invariant(tx.serverOnly?.executor, 'executor not found')
    return tx.serverOnly.executor as NonNullable<ServerOnlyParams['executor']>
  }

  function getRepo() {
    ensureBaseHubTransaction(tx)
    invariant(tx.serverOnly?.repo, 'repo not found')
    return tx.serverOnly.repo as NonNullable<ServerOnlyParams['repo']>
  }

  function repoId() {
    ensureBaseHubTransaction(tx)
    invariant(tx.serverOnly?.spaceID, 'spaceID not found')
    const parsed = parseSpaceName(tx.serverOnly.spaceID)
    invariant(parsed.spaceType === 'repo', 'spaceType should be repo')
    return parsed.repoId
  }

  if (!!baseBranch.archivedAt) {
    throw new Error('Branch is archived')
  }

  if (message.length > 2000) {
    throw new Error('Commit message should be less than 2000 characters.')
  }

  if (!stagedBlockIds.length) {
    throw new Error('No blocks were selected for this commit.')
  }

  const repo = getRepo()

  /**
   * Set of all working + head trees. We'll iterate over this set to create the new staged tree.
   * Why also head? To include deleted blocks, which the user might've not staged and therefore we'd need to include in the new staged tree.
   */
  const sourceBlockIdsToProcess = new Set<string>()
  console.log('[COMMIT MUTATOR] Loading tree managers', {
    commonAncestorRootBlockId: commonAncestor?.rootBlockId ?? null,
    headRootBlockId: head?.rootBlockId ?? null,
    sourceRootBlockId: source.rootBlockId,
  })
  const [
    commonAncestorCommitTreeManager,
    headCommitTreeManager,
    sourceTreeManager,
  ] = await Promise.all([
    commonAncestor ? tx.getTreeManager(commonAncestor.rootBlockId) : null,
    head ? tx.getTreeManager(head.rootBlockId) : null,
    tx.getTreeManager(source.rootBlockId),
  ])

  console.log('[COMMIT MUTATOR] Tree managers loaded', {
    hasCommonAncestorTreeManager: !!commonAncestorCommitTreeManager,
    hasHeadTreeManager: !!headCommitTreeManager,
    hasSourceTreeManager: !!sourceTreeManager,
    sourceTreeRootId: sourceTreeManager?.root?.value?.id ?? null,
    headTreeRootId: headCommitTreeManager?.root?.value?.id ?? null,
  })
  perfReporter.checkpoint('tree managers loaded')

  const changedSourceBlockIds = FINE_GRAINED_PROCESSING
    ? await tx.serverOnly.getChangedSourceBlockIds({
        sourceRootBlockId: source.rootBlockId,
        headRootBlockId: head?.rootBlockId ?? null,
        commonAncestorRootBlockId: commonAncestor?.rootBlockId ?? null,
        diffType: rest.type ?? 'commit',
      })
    : 'all'

  perfReporter.checkpoint('got changed source block ids')

  const allSourceBRsMap = await tx.getBrsByRootId(
    sourceTreeManager.root.value as RootBlock
  )
  const headBRsIndexedMap = headCommitTreeManager
    ? await tx.getBrsByRootId(headCommitTreeManager.root.value as RootBlock)
    : null
  const ancestorBRsIndexedMap = commonAncestorCommitTreeManager
    ? await tx.getBrsByRootId(
        commonAncestorCommitTreeManager.root.value as RootBlock
      )
    : null

  /**
   * Map to quickly match a BR from the head commit
   */
  const committedBRsMatcherMap = new Map<string, BlockRelationship>()
  const brsToExpire = new Set<string>()
  const blocksInNewCommitMap = createIndexedMap<Block>({
    autoFallbackKeys: ['sourceBlockId'],
  })
  const brsInNewCommitMap = createIndexedMap<
    BlockRelationship,
    Pick<CommonBRsCompoundIndexes, 'byParentId'>
  >({
    autoFallbackKeys: ['blockId', 'sourceBlockId'],
    compoundIndexes: {
      byParentId: brsByParentId,
    },
  })

  const joined = [
    ...allSourceBRsMap.values(),
    ...(headBRsIndexedMap?.values() ?? []),
  ]
  for (let i = 0; i < joined.length; i++) {
    const br = joined[i]
    if (br && !br.deletedAt && !isVirtualizedInstanceChildTargetBR(br)) {
      if (br.isCommitted) {
        const block = headCommitTreeManager?.getNode(br.blockId)?.value

        // this doesn't work well, because it can lead to conflicting BRs and blocks.
        if (
          block &&
          changedSourceBlockIds !== 'all' &&
          !changedSourceBlockIds.has(br.sourceBlockId)
        ) {
          // we can assume this block/br will be fully reused
          // so we can add it to the new commit map already and skip processing it
          blocksInNewCommitMap.set(br.blockId, block)
          brsInNewCommitMap.set(br.id, br)
          continue
        }

        committedBRsMatcherMap.set(hashBr(br), br)
        if (!br.validTo) {
          brsToExpire.add(br.id)
        }
      }
      sourceBlockIdsToProcess.add(br.sourceBlockId)
    }
  }

  perfReporter.checkpoint('got all source block ids')

  const summary: Record<
    // sourceBlockId
    string,
    {
      // sourceBlockId as well
      parentId: string | null
      status: 'created' | 'updated' | 'deleted'
    }
  > = {}

  const milestonesIndexedMap = createIndexedMap<BlockMilestone>()

  perfReporter.checkpoint('got all trees')

  /**
   * Get working root block + head commit root block (if a head commit exists!)
   */
  const sourceRootBlock = sourceTreeManager.root.value as RootBlock
  const sourceRootBr = allSourceBRsMap.get(source.rootBlockId)
  invariant(
    sourceRootBlock,
    `source root block ${source.rootBlockId} not found`
  )
  invariant(isRootBlock(sourceRootBlock), 'Working root block is not type root')
  invariant(
    sourceRootBr,
    `source root block relationship ${source.rootBlockId} not found`
  )

  console.log('[COMMIT MUTATOR] Source root block info', {
    sourceRootBlockId: sourceRootBlock.id,
    sourceRootBlockBranchId: sourceRootBlock.branchId,
    sourceRootBlockCreatedAt: sourceRootBlock.createdAt,
    sourceRootBrId: sourceRootBr.id,
    sourceBlockIdsToProcessCount: sourceBlockIdsToProcess.size,
    brsToExpireCount: brsToExpire.size,
  })

  await executor()
    .execute<Entry<BlockMilestone>>(
      sql`
      SELECT value, key, lastmodified
      FROM ${schema.entry}
      WHERE key LIKE 'milestone/%'
      AND spaceid = ${tx.serverOnly.spaceID}
      AND value->>'rootId' = ${sourceRootBlock.id}
      AND value->>'deletedAt' IS NULL
    `
    )
    .then((response) =>
      response.forEach((milestone) =>
        milestonesIndexedMap.set(milestone.value.id, milestone.value)
      )
    )

  const blocksToCreateMap = new Map<string, Block>()
  const brsToUpsertMap = new Map<string, BlockRelationship>()
  const dependenciesToRevalidate = new Set<string>()

  const blocksUnderRoot: Block[] = []
  // const sourceBlockOriginalHashMap = new Map<string, string>()
  const idSeed = id

  const commitContributors = new Set<string>()
  const milestonePromises: Map<string, () => Promise<void>> = new Map()
  let updatedBranchLineage = baseBranch.lineage
  if (rest.type === 'merge' && baseBranch.lineage) {
    updatedBranchLineage = []
    for (const l of baseBranch.lineage) {
      if (l.branchId === rest.sourceBranch.id) {
        // once i encounter the branch i'm merging from, i update the lastSyncedAt and re-inherit its lineage.
        updatedBranchLineage.push({ ...l, lastSyncedAt: createdAt })
        if (rest.sourceBranch.lineage) {
          updatedBranchLineage.push(...rest.sourceBranch.lineage)
        }
        // i stop the loop because i'm re-inheriting.
        break
      } else {
        updatedBranchLineage.push(l)
      }
    }
  }

  type ReuseMatch = {
    block: Block
    br: BlockRelationship | null
  }

  const newCommitRootBlockId = getId()

  const { block: srb } = getStagedBlockForCommit({
    rootBlockId: newCommitRootBlockId,
    rawStagedBlock: sourceRootBlock,
    rawStagedBr: sourceRootBr,
    sourceBlockId: idToSourceBlockId(sourceRootBlock),
    newBlockId: newCommitRootBlockId,
    newRootBlockId: newCommitRootBlockId,
    isoNow: createdAt,
    branchId: baseBranch.id,
    updatedBranchLineage,
  })
  invariant(isRootBlock(srb), 'Staged root block is not type root')
  const stagedRootBlock = srb

  const aggregates = {
    getStuff: 0,
    firstIdempotentIdThing: 0,
    isBlockStaged: 0,
    isBlockStaged__initial: 0,
    isBlockStaged__headCommit: 0,
    isBlockStaged__children: 0,
    mergeBlocks: 0,
    getMilestone: 0,
    milestoneUpdate: 0,
    getStagedBlockForCommit: 0,
    findExistingBlockAndBrToReuse: 0,
    findExistingBlockAndBrToReuse__matchAndClone: 0,
    findExistingBlockAndBrToReuse__matchBR: 0,
    deferredWork: 0,
    reusedBlock: 0,
    enhanceGeneratedHTML: 0,
  }

  async function findExistingBlockAndBrToReuse({
    stagedBlock,
    stagedBr,
  }: {
    stagedBlock: Block
    stagedBr: BlockRelationship
  }): Promise<ReuseMatch | null> {
    const sourceBlockId = stagedBlock.sourceBlockId || stagedBlock.id
    const sourceBrId = stagedBr.sourceBlockId || stagedBr.id
    if (!sourceBlockId || !sourceBrId) return null

    // Check head commit tree first
    let now = performance.now()
    // IMPORTANT: we should only reuse from the head commit; not from older commits. this gives us a good enough reutilization percentage, while keeping these lookups fast.
    // also, the gql cache depends on this fact (although it can probably be refactored to not depend on this).
    const blockFromCache = headCommitTreeManager?.getNode(sourceBlockId)?.value
    if (
      blockFromCache &&
      blockFromCache.isCommitted &&
      areBlocksCompatible(stagedBlock, blockFromCache)
    ) {
      const stagedBrClone = { ...stagedBr }
      aggregates.findExistingBlockAndBrToReuse__matchAndClone +=
        performance.now() - now
      now = performance.now()
      stagedBrClone.blockId = blockFromCache.id
      const brFromCache = committedBRsMatcherMap.get(hashBr(stagedBrClone))

      aggregates.findExistingBlockAndBrToReuse__matchBR +=
        performance.now() - now
      now = performance.now()

      if (
        blockFromCache &&
        brFromCache &&
        isBRInRoot(brFromCache, stagedRootBlock)
      ) {
        return { block: blockFromCache, br: brFromCache }
      } else {
        return { block: blockFromCache, br: null }
      }
    }

    return null
  }

  const stagedResultMap = new Map<string, boolean>()

  const isBlockStaged = (blockId: Block['id']): boolean => {
    if (stagedResultMap.has(blockId)) {
      return stagedResultMap.get(blockId) ?? false
    }

    let now = performance.now()

    const node = sourceTreeManager.getNode(blockId)

    let isStaged =
      (node && isRootBlock(node.value)) ||
      // user checked directly
      stagedBlockIds.includes(node?.value.id ?? blockId) ||
      stagedBlockIds.includes(node?.value.sourceBlockId ?? blockId) ||
      stagedBlockIds.includes(blockId) ||
      // user checked a parent directly
      sourceTreeManager
        .getPath(node ?? blockId, { format: 'array' })
        ?.some((id) => {
          const n = sourceTreeManager.getNode(id)
          if (!n) return false
          return (
            stagedBlockIds.includes(id) ||
            stagedBlockIds.includes(n.value.sourceBlockId ?? id)
          )
        }) === true

    aggregates.isBlockStaged__initial += performance.now() - now
    now = performance.now()

    if (!isStaged && headCommitTreeManager) {
      // check in commit tree. if the block was deleted, it won't be in the working tree.
      const nodeInHead = headCommitTreeManager?.getNode(blockId)
      headCommitTreeManager
        .getPath(nodeInHead ?? blockId, { format: 'array' })
        ?.forEach((id) => {
          invariant(
            headCommitTreeManager,
            'headCommitTreeManager shouls be defined'
          )
          const node = headCommitTreeManager.getNode(id)
          const sourceBlockId = node?.value.sourceBlockId ?? node?.value.id
          if (sourceBlockId) {
            const nodeInSourceTree = sourceTreeManager.getNode(sourceBlockId)
            if (
              stagedBlockIds.includes(sourceBlockId) ||
              (nodeInSourceTree &&
                (stagedBlockIds.includes(nodeInSourceTree.value.id) ||
                  stagedBlockIds.includes(
                    idToSourceBlockId(nodeInSourceTree.value)
                  )))
            ) {
              isStaged = true
            }
          }
        })

      aggregates.isBlockStaged__headCommit += performance.now() - now
      now = performance.now()

      if (!isStaged && !nodeInHead && node && isBlockWithChildren(node.value)) {
        // check if node is new and has at least a child has been staged
        isStaged =
          sourceTreeManager.getChildrenKeys(node.key)?.some(isBlockStaged) ??
          false
        aggregates.isBlockStaged__children += performance.now() - now
      }
    }

    stagedResultMap.set(blockId, isStaged)

    return isStaged
  }

  const isThreeWayMerge = Boolean(commonAncestorCommitTreeManager)
  // this will take a snapshot of the working root block for the commit
  const deferredWork: Array<
    (opts: {
      stagingTreeManager: TreeManager<
        Block,
        Pick<CommonBlocksCompoundIndexes, 'blocksByType'>
      >
    }) => Promise<void>
  > = []

  let blocksReusedCount = 0

  await pMap(Array.from(sourceBlockIdsToProcess), async (sourceBlockId) => {
    let now = performance.now()
    const sourceBlock = sourceTreeManager.getNode(sourceBlockId)?.value
    const sourceBr = allSourceBRsMap.get(sourceBlockId)
    const headBlock = headCommitTreeManager?.getNode(sourceBlockId)?.value // null if block has been created
    const headBr = headBRsIndexedMap?.get(sourceBlockId) ?? null
    const commonAncestorBlock =
      commonAncestorCommitTreeManager?.getNode(sourceBlockId)?.value
    const debug = false
    const commonAncestorBr = ancestorBRsIndexedMap?.get(sourceBlockId) ?? null

    aggregates.getStuff += performance.now() - now
    now = performance.now()

    if (debug) {
      // eslint-disable-next-line no-console
      console.log('debugging', sourceBlock?.title ?? 'untitled')
      // eslint-disable-next-line no-console
      console.log(
        JSON.stringify(
          {
            sourceBlock,
            sourceBr,
            headBlock,
            headBr,
            commonAncestorBlock,
            commonAncestorBr,
          },
          null,
          2
        )
      )
    }

    const newBlockId =
      sourceBlock?.type === 'root' ? newCommitRootBlockId : getId()

    aggregates.firstIdempotentIdThing += performance.now() - now
    now = performance.now()

    const isStaged = isBlockStaged(sourceBlockId ?? '')

    aggregates.isBlockStaged += performance.now() - now
    now = performance.now()

    let status: 'created' | 'updated' | 'deleted' | null = null
    // add to diff summary
    if (isStaged && sourceBlockId) {
      if (!sourceBlock && headBlock) {
        status = 'deleted'
      } else if (sourceBlock && !headBlock) {
        status = 'created'
      } else if (
        sourceBlock &&
        headBlock &&
        !areBlocksCompatible(sourceBlock, headBlock)
      ) {
        status = 'updated'
      }
      if (status) {
        summary[sourceBlockId] = {
          parentId: sourceBr?.parentId ?? null,
          status,
        }
      }
    }

    if (status) {
      dependenciesToRevalidate.add(sourceBlockId)
      const current = sourceBlock
        ? { block: sourceBlock, br: sourceBr! }
        : headBlock
          ? { block: headBlock, br: headBr! }
          : undefined
      if (current) {
        deferredWork.push(async ({ stagingTreeManager }) => {
          addToDependenciesToRevalidate({
            current,
            prev:
              sourceBlock && headBlock && headBr
                ? { block: headBlock, br: headBr }
                : undefined,
            treeManager: stagingTreeManager,
            dependenciesToRevalidate,
          })
        })
      }
    }

    if (debug) {
      // eslint-disable-next-line no-console
      console.log('isStaged', isStaged)
    }

    /**
     * Merge blocks.
     */
    let rawStagedBlock: Block | null = null
    let rawStagedBr: BlockRelationship | null = null
    if (isThreeWayMerge) {
      // this is a three-way merge
      const mergeResult = mergeBlocks({
        idSeed,
        now: createdAt,
        sourceRootBlock,
        source:
          sourceBlock && sourceBr ? { block: sourceBlock, br: sourceBr } : null,
        head: headBlock && headBr ? { block: headBlock, br: headBr } : null,
        commonAncestor:
          commonAncestorBlock && commonAncestorBr
            ? { block: commonAncestorBlock, br: commonAncestorBr }
            : null,
        resolveConflict: (ctx) => {
          invariant(rest.type === 'merge', 'expected merge')

          if (ctx.type === 'br-three-ways') {
            // resolution on three ways br merge we decide to favour the head
            return 'head'
          }
          const sourceBlockId =
            ctx.source?.br.sourceBlockId ||
            ctx.head?.br.sourceBlockId ||
            ctx.source?.block.sourceBlockId ||
            ctx.head?.block.sourceBlockId ||
            ctx.source?.block.id ||
            ctx.head?.block.id
          invariant(sourceBlockId, 'sourceBlockId should be defined')
          const conflictKey = sourceBlockId
          const conflictKeyRT = `${conflictKey}${
            typeof ctx.richTextNodeIndex === 'number'
              ? `/${ctx.richTextNodeIndex}`
              : ''
          }`
          const result =
            rest.conflictResolutions?.[conflictKeyRT] ??
            rest.conflictResolutions?.[conflictKey]
          if (result === 'base') return 'head'
          if (result === 'source') return 'source'

          return 'unresolved'
        },
      })

      if (debug) {
        // eslint-disable-next-line no-console
        console.log('mergeResult', JSON.stringify(mergeResult, null, 2))
      }

      switch (mergeResult.result) {
        case 'delete':
          rawStagedBlock = null
          rawStagedBr = null
          break
        case 'add':
          rawStagedBlock = mergeResult.value!.block
          rawStagedBr = mergeResult.value!.br
          break
        case 'merge':
          invariant(sourceBlock, `sourceBlock not found`)
          rawStagedBlock = mergeResult.value!.block
          rawStagedBr = mergeResult.value!.br
          break
        case 'conflict':
          throw new Error('conflicts: ' + JSON.stringify(mergeResult.conflicts))
      }
    } else {
      // normal two-way merge, favouring the source block if staged
      rawStagedBlock = (isStaged ? sourceBlock : headBlock) ?? null
      rawStagedBr = isStaged ? sourceBr : headBr
    }

    aggregates.mergeBlocks += performance.now() - now
    now = performance.now()

    if (!rawStagedBlock || !rawStagedBr) return { type: 'not-found' }

    invariant(sourceBlockId, 'Expected sourceBlockId to be defined')

    let forceNewBecauseChildChanged = false
    const sourceMilestone = isStaged
      ? milestonesIndexedMap.get(
          getMilestoneId({
            sourceBlockId,
            rootId: sourceRootBlock.id,
          })
        )
      : null

    aggregates.getMilestone += performance.now() - now
    now = performance.now()

    $milestoneUpdate: if (sourceMilestone) {
      // Milestones depend on each other (parents update when their direct children are modified)
      // We need to update the milestone if any of its dependencies are staged, and keep them as "WIP" otherwise
      const [stagedDeps, unstagedDeps] = sourceMilestone.dependencies.reduce(
        (acc, depMilestoneId) => {
          const match = milestonesIndexedMap.get(depMilestoneId)
          if (!match) return acc // non-existing milestone, probably a bug?
          if (isBlockStaged(match.sourceBlockId)) {
            return [acc[0].concat(depMilestoneId), acc[1]]
          } else {
            return [acc[0], acc[1].concat(depMilestoneId)]
          }
        },
        [[] as string[], [] as string[]]
      )

      // if no dependencies are staged, we ignore the update
      if (!stagedDeps.length) break $milestoneUpdate

      forceNewBecauseChildChanged = true
      const newMilestone: BlockMilestone = {
        ...structuredClone(sourceMilestone),
        branchId: baseBranch.id,
        commitId: id,
        createdAt,
        updatedAt: createdAt,
        blockId: newBlockId,
        rootId: newCommitRootBlockId,
        id: getMilestoneId({
          sourceBlockId,
          rootId: newCommitRootBlockId,
        }),
        dependencies: stagedDeps.map((mId) => {
          const match = milestonesIndexedMap.get(mId)
          invariant(match, 'match should be defined')
          return getMilestoneId({
            sourceBlockId: match.sourceBlockId,
            rootId: newCommitRootBlockId,
          })
        }),
      }

      switch (newMilestone.currentStatus) {
        case 'created': {
          newMilestone.contributors.deleted = null
          newMilestone.reordered = null
          break
        }
        case 'modified': {
          newMilestone.contributors.deleted = null
          if (newMilestone.contributors.created?.[0]) {
            newMilestone.currentStatus = 'created'
            newMilestone.reordered = null
          }
          break
        }
        case 'deleted': {
          newMilestone.contributors = {
            deleted: newMilestone.contributors.deleted,
          }
          newMilestone.reordered = null
          break
        }
      }

      for (const contributorId of flattenContributors(
        newMilestone.contributors
      )) {
        commitContributors.add(contributorId)
      }

      milestonePromises.set(newMilestone.id, async () => {
        // milestone could have staged and unstaged deps, if all are staged, we remove the WIP, if not, we keep it
        await blockMilestoneBaseOps.set(tx, newMilestone)
        if (unstagedDeps.length) {
          await blockMilestoneBaseOps.update(tx, {
            id: sourceMilestone.id,
            dependencies: unstagedDeps,
          })
        } else {
          await blockMilestoneBaseOps.update(tx, {
            id: sourceMilestone.id,
            deletedAt: createdAt,
          })
        }
      })
    }

    aggregates.milestoneUpdate += performance.now() - now
    now = performance.now()

    if (forceNewBecauseChildChanged) {
      rawStagedBlock.updatedAt = createdAt
      const existing = summary[sourceBlockId]
      if (!existing) {
        summary[sourceBlockId] = {
          status: 'updated',
          parentId: rawStagedBr.parentId,
        }
      }
    }

    // get staging block for commit
    const { block: stagedBlock, br: stagedBr } = getStagedBlockForCommit({
      rootBlockId: newCommitRootBlockId,
      rawStagedBlock,
      rawStagedBr,
      sourceBlockId,
      newBlockId,
      newRootBlockId: newCommitRootBlockId,
      isoNow: createdAt,
      branchId: baseBranch.id,
      updatedBranchLineage,
      playgroundId: tx.serverOnly?.playgroundId,
    })

    aggregates.getStagedBlockForCommit += performance.now() - now

    if (isRootBlock(stagedBlock)) {
      if (!isEqual(stagedBlock, stagedRootBlock)) {
        // eslint-disable-next-line no-console
        console.log(JSON.stringify({ stagedRootBlock, stagedBlock }, null, 2))
        throw new Error('stagedRootBlock is not equal to stagedBlock')
      }
    }

    now = performance.now()
    const existingMatch = isRootBlock(stagedBlock)
      ? null // root blocks are never reused
      : forceNewBecauseChildChanged
        ? null
        : await findExistingBlockAndBrToReuse({ stagedBlock, stagedBr })

    aggregates.findExistingBlockAndBrToReuse += performance.now() - now
    now = performance.now()

    if (debug) {
      // eslint-disable-next-line no-console
      console.log('existingMatch', existingMatch)
      // eslint-disable-next-line no-console
      console.log('stagedBlock', stagedBlock)
      // eslint-disable-next-line no-console
      console.log('stagedBr', stagedBr)
    }

    if (existingMatch) {
      blocksUnderRoot.push(existingMatch.block)
      blocksInNewCommitMap.set(existingMatch.block.id, existingMatch.block)
      if (existingMatch.br) {
        brsToExpire.delete(existingMatch.br.id)
        brsInNewCommitMap.set(existingMatch.br.id, existingMatch.br)
      } else {
        stagedBr.blockId = existingMatch.block.id
        brsToUpsertMap.set(stagedBr.id, stagedBr)
        brsInNewCommitMap.set(stagedBr.id, stagedBr)
      }

      aggregates.reusedBlock += performance.now() - now
      now = performance.now()

      blocksReusedCount++

      return {
        type: 'reused',
        data: { id: existingMatch.block.id },
      }
    }

    if (isRichTextBlock(stagedBlock)) {
      const now = performance.now()
      // pre-compute common api transformations here (html in this case)
      const _json = cloneDeep(stateToJson(stagedBlock.value.state))
      const json = (await proxyRichTextAssets(_json as any, repoId())) as any

      const isRichTextEmpty = isEmpty(json)
      let html = tipTapJSONToHTMLString({
        isEmpty: !!isRichTextEmpty,
        // clone before transforming to avoid mutating the original json
        json,
        extensions: richTextExtensionsForTransformations,
      })
      html =
        (await enhanceGeneratedHTML({
          html,
          options: {
            slugs: true,
            toc: undefined,
          },
        })) || html
      stagedBlock.value.transformations = { version: 1, html }
      aggregates.enhanceGeneratedHTML += performance.now() - now
    }

    if (!isRootBlock(stagedBlock)) blocksUnderRoot.push(stagedBlock)
    blocksToCreateMap.set(stagedBlock.id, stagedBlock)
    blocksInNewCommitMap.set(stagedBlock.id, stagedBlock)
    brsToUpsertMap.set(stagedBr.id, stagedBr)
    brsInNewCommitMap.set(stagedBr.id, stagedBr)

    deferredWork.push(async ({ stagingTreeManager }) => {
      const now = performance.now()

      if (
        (isReferenceBlock(stagedBlock) || isRichTextBlock(stagedBlock)) &&
        isThreeWayMerge
      ) {
        /**
         * We'll make sure target blocks and accepted types are "resolved".
         * This is a patch just for the ref block for now, but we should make something more scalable/robust in the future.
         * The idea is that in the base branch, the user could've removed a type option, but due to how our array merging logic works,
         * we'll concatenate the arrays, and therefore the removed type option will still be there. Here we'll clean it up.
         */

        let changed = false
        if (isReferenceBlock(stagedBlock)) {
          stagedBlock.constraints.acceptedTypes =
            stagedBlock.constraints.acceptedTypes?.filter((t) => {
              const comp = stagingTreeManager.getNode(t)
              if (!comp) {
                changed = true
                return false
              }
              return true
            }) ?? null
          stagedBlock.value.targetBlockIds =
            stagedBlock.value.targetBlockIds?.filter((t) => {
              const target = stagingTreeManager.getNode(t)
              if (!target) {
                changed = true
                return false
              }
              return true
            }) ?? null
        }

        if (isRichTextBlock(stagedBlock)) {
          stagedBlock.constraints.allowedComponents =
            stagedBlock.constraints.allowedComponents?.filter((t) => {
              const comp = stagingTreeManager.getNode(t)
              if (!comp) {
                changed = true
                return false
              }
              return true
            }) ?? null
        }

        if (changed) {
          try {
            stagingTreeManager.updateNode(stagedBlock.id, {
              value: stagedBlock,
            })
          } catch (err) {
            // This block might be not referenced in the new tree,
            // if it's an instance child and it's target was deleted in the component
          }
        }
      }

      if (stagedBlock.type !== 'root') {
        addToDependenciesToRevalidate({
          current: { block: stagedBlock, br: stagedBr },
          prev:
            headBlock && headBr ? { block: headBlock, br: headBr } : undefined,
          treeManager: stagingTreeManager,
          dependenciesToRevalidate,
        })
      }
      aggregates.deferredWork += performance.now() - now
    })

    return {
      type: 'created',
      data: { id: newBlockId },
    }
  })

  logger(aggregates)
  console.log('[COMMIT MUTATOR] Finished processing source blocks', {
    blocksToCreateCount: blocksToCreateMap.size,
    brsToUpsertCount: brsToUpsertMap.size,
    blocksInNewCommitCount: blocksInNewCommitMap.size,
    brsInNewCommitCount: brsInNewCommitMap.size,
    deferredWorkCount: deferredWork.length,
    blocksReusedCount,
    summaryEntriesCount: Object.keys(summary).length,
  })
  perfReporter.checkpoint('saved all blocks for commit')

  const stagingTreeManager = getTreeManager<
    Block,
    Pick<CommonBlocksCompoundIndexes, 'blocksByType'>
  >({
    rootKey: stagedRootBlock.id,
    getValue: (key) => {
      const block = blocksInNewCommitMap.get(key)
      invariant(block, `here: block ${key} not found in cache`)
      return block
    },
    getChildrenKeys: (_key, block) => {
      return getChildrenKeys({
        block,
        blockRelationshipsMap: brsInNewCommitMap,
        blocksMap: blocksInNewCommitMap,
        rootBlockId: stagedRootBlock.id,
      })
    },
    getOrder: (key) => {
      const br = brsInNewCommitMap.get(key)
      invariant(br, `block relationship ${key} not found in cache`)
      return br.order
    },
    getKey: (block) => block.id,
    indexKeys: ['sourceBlockId'],
    autoFallbackWithKeys: ['sourceBlockId'],
    compoundIndexes: commonBlocksCompoundIndexes,
    onChange: (props) => {
      const handler = getOnChangeHandler({
        blockRelationshipsMap: brsInNewCommitMap,
        blocksMap: blocksInNewCommitMap,
      })

      handler(props)
    },
  })

  logger('STAGING TREE MANAGER WAS BUILT')

  await Promise.all(
    deferredWork.map(async (work) => await work({ stagingTreeManager }))
  )
  logger('DEFERRED WORK WAS DONE')

  perfReporter.checkpoint('built staging tree')

  if (!testMode) {
    invariant(usageInfo, 'usageInfo is required')
  }
  const usageResult = testMode
    ? ({ allowed: true, newBlocksUsage: {} } as ReturnType<
        typeof checkUsageAllowsCommit
      >)
    : checkUsageAllowsCommit({
        ...usageInfo!,
        stagingTreeManagerAboutToCommit: stagingTreeManager,
        repoId: repoId(),
      })
  logger('BLOCK USAGE WAS CHECKED')

  if (usageResult.allowed === false) {
    throw new CustomBlocksUsageError(usageResult.reason)
  }

  perfReporter.checkpoint('usage checked')

  const { matches, errors } = checkBlocksMatchAllConstraints({
    treeNode: stagingTreeManager.root,
    treeManager: stagingTreeManager,
    sourceBlockIdsToProcess,
  })

  logger('CONSTRAINTS WERE CHECKED')

  if (!matches) {
    if (errors.length > 0) {
      console.error('[COMMIT MUTATOR] Commit failed with constraint errors', {
        errorsCount: errors.length,
        errors: errors.map((e) => ({
          type: e.type,
          blockId: 'blockId' in e ? e.blockId : undefined,
        })),
      })
      throw new CustomErrorCommit(errors)
    }

    console.error('[COMMIT MUTATOR] Commit failed - matches is false but no errors')
    throw new Error('Commit failed')
  }

  perfReporter.checkpoint('checked constraints')

  logger(
    `WILL CREATE ${blocksToCreateMap.size} BLOCKS AND ${brsToUpsertMap.size} BRs FOR THIS COMMIT`
  )

  console.log('[COMMIT MUTATOR] Starting to save blocks and BRs to DB', {
    blocksToCreate: blocksToCreateMap.size,
    brsToUpsert: brsToUpsertMap.size,
    brsToExpire: brsToExpire.size,
    milestonesToSave: milestonePromises.size,
  })

  // save new blocks to db
  const saveNewBlocksToDbPromise = Promise.all(
    Array.from(blocksToCreateMap.values()).map(async (block) => {
      try {
        await blockBaseOps.put(tx, block, null)
      } catch (err) {
        console.error('[COMMIT MUTATOR] ERROR SAVING BLOCK TO DB', {
          blockId: block.id,
          blockType: block.type,
          error: err,
        })
        throw err
      }
    })
  )

  const saveNewBRsToDbPromise = Promise.all(
    Array.from(brsToUpsertMap.values()).map(async (br) => {
      await blockRelationshipBaseOps.set(tx, br)
    })
  )

  const saveNewMilestonesToDbPromise = Array.from(
    milestonePromises.values()
  ).map((fn) => fn())

  commitContributors.delete(authorId)
  const createdAtMinusMs = subMilliseconds(new Date(createdAt), 1)
  const expireBRsPromise = Promise.all(
    Array.from(brsToExpire).map(async (brId) => {
      const br = await blockRelationshipBaseOps.get(tx, brId)
      invariant(br, `BR ${brId} not found`)
      if (br.branchId === stagedRootBlock.branchId) {
        await blockRelationshipBaseOps.update(tx, {
          id: brId,
          validTo: createdAtMinusMs.toISOString(),
        })
      } else {
        /**
         * do a lil copy-on-write instead, as we don't want to mess with the head branch
         * not a copy-on-write, but actually a placeholder thing
         * we don't expire it, as we want it as an override
         * if it exists in the new commit, then we do nothing, as it'll already act as an override
         * else we create it as a placeholder with placeholderSince
         */
        const existsInNewCommit = brsInNewCommitMap.get(br.sourceBlockId)
        if (existsInNewCommit) return
        const placeholder = {
          ...br,
          branchId: stagedRootBlock.branchId,
          id: getBRId(br.blockId, stagedRootBlock.id),
          validFrom: createdAt,
          placeholderSince: createdAt,
        }
        delete placeholder.validTo
        await blockRelationshipBaseOps.set(tx, placeholder)
      }
    })
  )

  console.log('[COMMIT MUTATOR] Creating new commit object', {
    commitId: id,
    rootBlockId: newCommitRootBlockId,
    parentCommitId: baseBranch.headCommitId,
    branchId: baseBranch.id,
    contributorsCount: commitContributors.size,
  })

  const newCommit: Commit = {
    branchId: baseBranch.id,
    ...(updatedBranchLineage ? { branchLineage: updatedBranchLineage } : {}),
    id,
    createdAt,
    message,
    authorId,
    rootBlockId: newCommitRootBlockId,
    parentCommitId: baseBranch.headCommitId,
    repoId: repoId(),
    contributors: Array.from(commitContributors),
    ...(tx.serverOnly.playgroundId
      ? { playgroundId: tx.serverOnly.playgroundId }
      : {}),
  }

  if (rest.type === 'merge') {
    newCommit.mergeParentCommitId =
      rest.sourceBranch.headCommitId ?? rest.sourceBranch.headCommit?.id ?? null
  }

  const updatedContributors = Array.from(
    new Set([...(baseBranch?.contributors ?? []), authorId])
  )
  const updatedBranch: Branch = {
    ...baseBranch,
    ...(updatedBranchLineage ? { lineage: updatedBranchLineage } : {}),
    headCommitId: id,
    headCommit: newCommit,
    updatedAt: createdAt,
    contributors: updatedContributors,
  }
  await Promise.all([
    branchBaseOps.update(tx, updatedBranch),
    commitBaseOps.put(tx, newCommit),
    saveNewBlocksToDbPromise,
    saveNewBRsToDbPromise,
    saveNewMilestonesToDbPromise,
    expireBRsPromise,
    advanceRootBlockPostCommit(tx, {
      rootBlockId: sourceTreeManager.root.value.id,
      iso: stagingTreeManager.root.value.createdAt,
    }),
  ])

  perfReporter.checkpoint('saved commit to db')
  const report = perfReporter.end()

  // eslint-disable-next-line no-console
  console.log(
    `Report for commit ${id}: ${JSON.stringify(
      {
        report,
        blocksCreated: blocksToCreateMap.size,
        brsUpserted: brsToUpsertMap.size,
        blocksReusedCount,
      },
      null,
      2
    )}`
  )

  // eslint-disable-next-line no-console
  console.log('COMMIT WAS SUCCESSFUL')

  // build actions array
  const actions: WorkflowActionReady[] = []
  const eventSubscribers: Array<EventSubscriber> = []
  const blocksByType = (stagingTreeManager.compoundIndexes.get(
    'blocksByType'
  ) ?? new Map()) as Map<BlockType, Set<string>>
  const workflowBlockIds = blocksByType.get('workflow') ?? new Set()
  const summaryChildrenByParentId = new Map<string, Set<string>>()
  Object.entries(summary).forEach(([id, child]) => {
    if (child.parentId) {
      const existingSet =
        summaryChildrenByParentId.get(child.parentId) ?? new Set()
      existingSet.add(id)
      summaryChildrenByParentId.set(child.parentId, existingSet)
    }
  })

  for (const id of workflowBlockIds) {
    const node = stagingTreeManager.getNode(id, { required: true })
    invariant(isWorkflowBlock(node.value), 'Expected workflow block')
    const sourceBlockId = idToSourceBlockId(node.value)
    const workflowBlockPath = stagingTreeManager.getPath(node.key, {
      format: 'string',
    })
    invariant(
      workflowBlockPath,
      `Expected workflowBlock(id: ${sourceBlockId}) path to be defined`
    )

    const subActions: Array<{
      localOnly?: boolean
      idempotencyKeyBase: string
      payload: (
        | Extract<WebhookActionReady, { type: 'webhook' }>
        | Extract<WebhookActionReady, { type: 'notification' }>
      )['payload']
    }> = []
    let newEventSubscriber: EventSubscriber | null = null

    const trigger = node.value.value.trigger
    switch (trigger?.type) {
      case 'commit':
        subActions.push({
          idempotencyKeyBase: newCommit.id,
          payload: {
            timestamp: createdAt,
            type: 'commit.created',
            data: newCommit,
          },
        })
        break
      case 'list-block': {
        const listNode = stagingTreeManager.getNode(trigger.blockId)
        if (!listNode) continue
        invariant(isListBlock(listNode.value), 'Expected list block')

        const summaryChildren = [
          ...(summaryChildrenByParentId.get(
            idToSourceBlockId(listNode.value)
          ) ?? []),
        ]
        const created: string[] = []
        const updated: string[] = []
        const deleted: string[] = []
        summaryChildren.forEach((childId) => {
          const child = summary[childId]
          if (!child) return
          switch (child?.status) {
            case 'created':
              created.push(childId)
              break
            case 'updated':
              updated.push(childId)
              break
            case 'deleted':
              deleted.push(childId)
              break
            default:
              break
          }
        })

        const listBlockTitle = listNode.value.title || 'Untitled'

        for (const event of trigger.events ?? []) {
          switch (event) {
            case 'created': {
              created.forEach((newRowId) => {
                subActions.push({
                  idempotencyKeyBase: `${newCommit.id}:${newRowId}`,
                  payload: {
                    timestamp: createdAt,
                    type: 'list-block.created',
                    data: {
                      listBlockId: idToSourceBlockId(listNode.value),
                      listBlockTitle,
                      blockId: newRowId,
                    },
                  },
                })
              })
              break
            }
            case 'updated': {
              updated.forEach((updatedRowId) => {
                subActions.push({
                  idempotencyKeyBase: `${newCommit.id}:${updatedRowId}`,
                  payload: {
                    timestamp: createdAt,
                    type: 'list-block.updated',
                    data: {
                      listBlockId: idToSourceBlockId(listNode.value),
                      listBlockTitle,
                      blockId: updatedRowId,
                    },
                  },
                })
              })
              break
            }
            case 'deleted': {
              deleted.forEach((deletedRowId) => {
                subActions.push({
                  idempotencyKeyBase: `${newCommit.id}:${deletedRowId}`,
                  payload: {
                    timestamp: createdAt,
                    type: 'list-block.deleted',
                    data: {
                      listBlockId: idToSourceBlockId(listNode.value),
                      listBlockTitle,
                      blockId: deletedRowId,
                    },
                  },
                })
              })
              break
            }
            default:
              break
          }
        }
        break
      }
      case 'event-block': {
        const eventNode = stagingTreeManager.getNode(trigger.blockId)
        if (!eventNode?.parentKey) continue
        invariant(isEventBlock(eventNode.value), 'Expected event block')
        const parentNode = stagingTreeManager.getNode(eventNode.parentKey)
        if (!parentNode) continue

        const eventBlockTitle = eventNode.value.title || 'Untitled'

        // at commit time, our job is to create an "event subscriber" in clickhouse
        // (will be done in the webhook)
        newEventSubscriber = {
          repoId: repoId(),
          blockId: trigger.blockId,
          commitId: newCommit.id,
          branch: updatedBranch.name,
          createdAt,
          data: {
            type: 'workflow',
            actions: [],
          },
        }
        subActions.push({
          localOnly: true,
          idempotencyKeyBase: `${newCommit.id}:${trigger.blockId}`,
          payload: {
            type: 'event-block.created',
            timestamp: createdAt,
            data: {
              eventBlockId: trigger.blockId,
              parentBlockId: idToSourceBlockId(parentNode.value),
              eventBlockTitle,
              data: null,
            },
          },
        })
        break
      }
      default:
        break
    }
    if (subActions.length === 0) continue

    const localActions: Array<WorkflowActionReady> = []

    Object.entries(node.value.value.actions).forEach(([actionKey, _action]) => {
      invariant(isWorkflowBlock(node.value), 'Expected workflow block')
      subActions.map((subAction) => {
        let action: WorkflowActionReady | null = null
        const idempotencyKey = actionKey + ':' + subAction.idempotencyKeyBase
        invariant(tx.serverOnly?.spaceID, 'spaceID not found')
        switch (_action.type) {
          case 'webhook': {
            const url = resolveWebhookUrl({
              url: _action.url,
              blockId: sourceBlockId,
              treeManager: stagingTreeManager,
              origin: getOriginUrl({ branch: updatedBranch, repo }),
            })
            try {
              // lil validations
              if (!url) break
              new URL(url)
            } catch (err) {
              break
            }
            action = {
              type: 'webhook',
              branchId: updatedBranch.id,
              url,
              blockId: sourceBlockId,
              idempotencyKey,
              payload: subAction.payload,
              repoId: repoId(),
            }
            break
          }
          case 'notification':
            if (subAction.payload.type === 'block.updated') break
            action = {
              type: 'notification',
              userIds: _action.userIds,
              repoId: repoId(),
              branchId: updatedBranch.id,
              idempotencyKey,
              blockId: sourceBlockId,
              payload: subAction.payload,
            }
            break
          default:
            break
        }
        if (action) {
          if (!subAction.localOnly) {
            actions.push(action)
          }
          localActions.push(action)
        }
      })
    })

    if (newEventSubscriber) {
      newEventSubscriber.data = { type: 'workflow', actions: localActions }
      eventSubscribers.push(newEventSubscriber)
    }
  }

  if (actions.length > 0) {
    logger(JSON.stringify({ actions }, null, 2))
  }

  return {
    actions,
    eventSubscribers,
    skipWorkflows,
    branch: updatedBranch,
    commit: newCommit,
    newBlocksUsage: usageResult.newBlocksUsage,
    stagingBrs: Array.from(brsInNewCommitMap.values()),
    treeManagers: {
      dependenciesToRevalidate: {
        cacheKey: getResolvedRefGqlCacheId({
          rootBlock: stagingTreeManager.root.value as RootBlock,
          resolvedRef: updatedBranch,
        }),
        deps: dependenciesToRevalidate,
      },
      allSourceBRsMap,
      headBRsIndexedMap,
      ancestorBRsIndexedMap,
      stagingBRsIndexedMap: brsInNewCommitMap,
      stagingBlocksIndexedMap: blocksInNewCommitMap,
      stagingTreeManager,
      headCommitTreeManager,
      commonAncestorCommitTreeManager,
    } satisfies CommitTreeManagers_NonSerializable as any,
  }
}

export type CommitTreeManagers_NonSerializable = {
  dependenciesToRevalidate: { cacheKey: string; deps: Set<string> }
  allSourceBRsMap: IndexedMap<
    BlockRelationship,
    Pick<CommonBRsCompoundIndexes, 'byParentId'>
  >
  headBRsIndexedMap: IndexedMap<
    BlockRelationship,
    Pick<CommonBRsCompoundIndexes, 'byParentId'>
  > | null
  ancestorBRsIndexedMap: IndexedMap<
    BlockRelationship,
    Pick<CommonBRsCompoundIndexes, 'byParentId'>
  > | null
  stagingBRsIndexedMap: IndexedMap<
    BlockRelationship,
    Pick<CommonBRsCompoundIndexes, 'byParentId'>
  >
  stagingBlocksIndexedMap: IndexedMap<Block, NonNullable<unknown>>
  stagingTreeManager: TreeManager<Block, NonNullable<unknown>>
  headCommitTreeManager: TreeManager<Block, NonNullable<unknown>> | null
  commonAncestorCommitTreeManager: TreeManager<
    Block,
    NonNullable<unknown>
  > | null
}

commit.rollbackOnError = true

/**
 * Resets working state back to the last commit. If there's no "last commit", then it deletes every block but the root block.
 * This mutator shouldn't be called from the client, as it'll access the DB to create the last commit tree.
 */
const discardWorkingChanges = async (
  tx: WriteTransaction,
  {
    workingRootBlockId,
    branchId,
    idsToDiscard,
  }: WithServerSecret<{
    workingRootBlockId: string
    branchId: string
    idsToDiscard: string[] | 'all'
  }>
) => {
  ensureBaseHubTransaction(tx)
  serverAuthorizedOrThrow(tx.serverSecret)
  invariant(tx.serverOnly?.spaceLineage, 'spaceLineage not found')

  // early return if there's no changes to make
  if (idsToDiscard.length === 0 && idsToDiscard !== 'all') return

  const perfReporter = beginPerfReport('discard working changes')

  function executor() {
    ensureBaseHubTransaction(tx)
    invariant(isServer, 'executor() should only be called on the server')
    invariant(tx.serverOnly?.executor, 'executor not found')
    return tx.serverOnly.executor as NonNullable<ServerOnlyParams['executor']>
  }

  function branch() {
    ensureBaseHubTransaction(tx)
    invariant(tx.serverOnly?.resolvedRef, 'resolvedRef not found')
    invariant(
      isBranch(tx.serverOnly.resolvedRef.value),
      'resolvedRef is not a branch'
    )
    invariant(
      branchId === tx.serverOnly.resolvedRef.value.id,
      'branchId does not match resolvedRef'
    )
    return tx.serverOnly.resolvedRef.value
  }

  perfReporter.checkpoint('got branch and working tree manager')

  invariant(workingRootBlockId, `Working root block not found in branch`)

  const now = new Date().toISOString()
  const workingBranch = branch()

  const idsToDiscardClause =
    idsToDiscard === 'all'
      ? sql``
      : sql`AND value->>'sourceBlockId' = ANY(ARRAY[`
          .append(
            sql.join(
              idsToDiscard.map((id) => sql`${id}`),
              sql.raw(',')
            )
          )
          .append(sql.raw('])'))

  const entries = await executor().execute<schema.Entry>(
    sql`
      select * from entry
      WHERE key LIKE 'br/%'
        AND spaceid = ${tx.serverOnly?.spaceID}
        AND value->>'branchId' = ${workingBranch.id}
        AND value->>'isCommitted' = 'false'
        AND value->>'blockId' != ${workingRootBlockId}
        ${idsToDiscardClause}

    union

     select * from entry
      WHERE key like 'milestone/%'
        AND spaceid = ${tx.serverOnly?.spaceID}
        AND value->>'branchId' = ${branchId}
        AND value->>'commitId' IS NULL
        ${idsToDiscardClause}

    union

    select * from entry
    where key = ${`block/${workingRootBlockId}`}
    and spaceid = ${tx.serverOnly?.spaceID}

    union

    select * from entry
    where key like 'br/%'
    and spaceid = ${tx.serverOnly?.spaceID}
    and value->>'blockId' = ${workingRootBlockId}
    and value->>'branchId' = ${branchId}
    `
  )

  perfReporter.checkpoint('got all entries needed')

  await pMap(entries, async (entry) => {
    // @ts-ignore
    tx._cache.set(entry.key, { dirty: false, value: entry.value })

    if (entry.key === `block/${workingRootBlockId}`) {
      // add the last reverted so that clients can re-build the tree from scratch
      const block = entry.value as Block
      await blockBaseOps.update(tx, {
        id: block.id,
        lastRevertedAt: now,
        branchId: workingBranch.id,
        ...(workingBranch.lineage
          ? { branchLineage: workingBranch.lineage }
          : {}),
      })
    } else if (entry.key.startsWith('br/')) {
      const br = entry.value as BlockRelationship
      if (br.blockId === workingRootBlockId) {
        // and update to the root block's BR so that our pull() logic will re-fetch it
        await blockRelationshipBaseOps.update(tx, {
          id: br.id,
          branchId: workingBranch.id,
        })
      } else {
        // mark matching BRs as discarded
        await blockRelationshipBaseOps.update(tx, {
          id: br.id,
          discardedAt: now,
        })
      }
    } else if (entry.key.startsWith('milestone/')) {
      // remove matching wip milestones
      const milestone = entry.value as BlockMilestone
      await blockMilestoneBaseOps.update(tx, {
        id: milestone.id,
        deletedAt: now,
      })
    } else {
      throw new Error(`unknown entry key at discard entry ${entry.key}`)
    }
  })

  perfReporter.checkpoint('done')

  perfReporter.end(true)
}

discardWorkingChanges.rollbackOnError = true

/**
 * Hard content reset: points the branch to a specific commit and discards all working changes.
 * This is a destructive operation that cannot be undone.
 */
const hardContentReset = async (
  tx: WriteTransaction,
  {
    branchId,
    commitId,
  }: WithServerSecret<{
    branchId: string
    commitId: string
  }>
) => {
  ensureBaseHubTransaction(tx)
  serverAuthorizedOrThrow(tx.serverSecret)
  invariant(tx.serverOnly?.spaceLineage, 'spaceLineage not found')

  const perfReporter = beginPerfReport('hard content reset')

  function branch() {
    ensureBaseHubTransaction(tx)
    invariant(tx.serverOnly?.resolvedRef, 'resolvedRef not found')
    invariant(
      isBranch(tx.serverOnly.resolvedRef.value),
      'resolvedRef is not a branch'
    )
    invariant(
      branchId === tx.serverOnly.resolvedRef.value.id,
      'branchId does not match resolvedRef'
    )
    return tx.serverOnly.resolvedRef.value
  }

  const workingBranch = branch()
  invariant(
    workingBranch.workingRootBlockId,
    'Working root block not found in branch'
  )

  const commit = await commitBaseOps.get(tx, commitId)
  invariant(commit, 'Commit not found')

  perfReporter.checkpoint('got branch')

  // Step 1: Update the branch to point to the specified commit (mutate as to update serverOnly params)
  workingBranch.headCommitId = commitId
  workingBranch.headCommit = commit
  workingBranch.updatedAt = new Date().toISOString()

  if (workingBranch.rollback) {
    // check if the rollback should be cleared (if it's in the future)
    const rollbackCommit = await commitBaseOps.get(
      tx,
      workingBranch.rollback.commitId
    )
    if (
      !rollbackCommit ||
      rollbackCommit.id === commit.id ||
      isBefore(new Date(commit.createdAt), new Date(rollbackCommit.createdAt))
    ) {
      workingBranch.rollback = null
    }
  }

  await branchBaseOps.update(tx, workingBranch)
  perfReporter.checkpoint('updated branch head commit')

  // Step 2: Advance the root block to the commit's date
  await advanceRootBlockPostCommit(tx, {
    rootBlockId: workingBranch.workingRootBlockId,
    iso: commit.createdAt,
  })

  // Step 3: Discard all working changes
  await discardWorkingChanges(tx, {
    workingRootBlockId: workingBranch.workingRootBlockId,
    branchId,
    idsToDiscard: 'all',
    serverSecret: tx.serverSecret,
  })

  perfReporter.checkpoint('discarded working changes')
  perfReporter.end(true)

  return { branch: workingBranch }
}

hardContentReset.rollbackOnError = true

/**
 * Merges a branch against its base.
 * This is the same as doing commit() against the base branch.
 */
export type MergeMutationInput = WithServerSecret<{
  sourceBranch: Branch
  baseBranch: Branch
  headCommit: Commit | null
  commonAncestorCommit: Commit | null
  rootBlockId: string
  contentRequest: ContentRequest
  usageInfo: CommitMutationInput['usageInfo']
  serverSecret: string
  type: 'merge' | 'update'
  authorId: string | undefined
  testMode?: boolean
  debug?: boolean
}>

const mergeCommit = async (
  tx: WriteTransaction,
  {
    contentRequest,
    rootBlockId,
    sourceBranch,
    baseBranch,
    headCommit,
    commonAncestorCommit,
    usageInfo,
    type,
    authorId,
    testMode,
    debug,
  }: MergeMutationInput
) => {
  console.log('[mergeCommit mutator] Starting merge commit', {
    type,
    contentRequestId: contentRequest.id,
    rootBlockId,
    sourceBranchId: sourceBranch.id,
    sourceBranchName: sourceBranch.name,
    baseBranchId: baseBranch.id,
    baseBranchName: baseBranch.name,
    headCommitId: headCommit?.id,
    commonAncestorCommitId: commonAncestorCommit?.id,
    authorId,
  })

  ensureBaseHubTransaction(tx)

  const perfReporter = beginPerfReport('mergeCommit', {
    logOnCheckpoint: true,
  })

  if (!testMode) {
    serverAuthorizedOrThrow(tx.serverSecret)
  }
  invariant(tx.serverOnly, 'serverOnly should be true')
  invariant(tx.serverSecret, 'serverSecret should be defined')
  invariant(isServer, 'merge() should only be called on the server')
  invariant(
    baseBranch.workingRootBlockId,
    'baseBranch.workingRootBlockId not found?'
  )

  console.log('[mergeCommit mutator] Authorization passed', {
    type,
    baseBranchWorkingRootBlockId: baseBranch.workingRootBlockId,
  })

  let conflictResolutions: ContentRequest['conflictResolutions'] = {}
  if (type === 'update') {
    // reverse conflict resolutions
    conflictResolutions = Object.fromEntries(
      Object.entries(contentRequest.conflictResolutions ?? {}).map(
        ([sourceBlockId, resolution]) => [
          sourceBlockId,
          resolution === 'base' ? 'source' : 'base',
        ]
      )
    )
  }

  const commitInput: CommitMutationInput = {
    debug,
    testMode,
    type: 'merge',
    id: getId(),
    authorId: authorId ?? contentRequest.authorId,
    conflictResolutions,
    usageInfo,
    message: `Merge branch '${sourceBranch.name}' into ${baseBranch.name}`,
    createdAt: new Date().toISOString(),
    baseBranch,
    sourceBranch,
    source: { rootBlockId },
    head: headCommit
      ? { commitId: headCommit.id, rootBlockId: headCommit.rootBlockId }
      : null,
    commonAncestor: commonAncestorCommit
      ? {
          commitId: commonAncestorCommit.id,
          rootBlockId: commonAncestorCommit.rootBlockId,
        }
      : null,
    stagedBlockIds: [rootBlockId],
    serverSecret: tx.serverSecret,
  }

  console.log('[mergeCommit mutator] Calling commit with merge input', {
    type,
    commitId: commitInput.id,
    message: commitInput.message,
    baseBranchId: baseBranch.id,
    sourceBranchId: sourceBranch.id,
  })

  const commitOutput = await commit(tx, commitInput)

  console.log('[mergeCommit mutator] Commit created', {
    type,
    commitId: commitOutput.commit.id,
    commitRootBlockId: commitOutput.commit.rootBlockId,
    branchId: commitOutput.branch.id,
    branchName: commitOutput.branch.name,
  })

  perfReporter.checkpoint('created merge commit')

  /**
   * After committing, we also want to fast-forward the working tree on the base branch with the new commit.
   *
   * We'll get the newly committed tree and merge it against the working tree. We'll auto-solve conflicts by keeping the working tree's version always.
   * We'll need to do a three-way merge using headCommit as the common ancestor.
   *
   * Now:
   * (main/working)     W
   * (main) A -- B -- C -- DC
   * (feature)        D
   *
   * After this:
   * (main/working)           W
   * (main) A -- B -- C -- DC
   * (feature)        D
   *
   */

  const {
    stagingBRsIndexedMap: newCommitBRsIndexedMap,
    stagingTreeManager: newCommitTreeManager,
  } = commitOutput.treeManagers as CommitTreeManagers_NonSerializable

  /**
   * in fast-forward, our ancestor is the old head commit,
   * as both the working and the newly committed head (staged) have it as its parent.
   */
  const ancestorCommitTreeManager = baseBranch.headCommit
    ? await tx.getTreeManager(baseBranch.headCommit.rootBlockId)
    : null
  const ancestorBRsIndexedMap = ancestorCommitTreeManager
    ? await tx.getBrsByRootId(ancestorCommitTreeManager.root.value as RootBlock)
    : null

  perfReporter.checkpoint('trees')

  tx.serverOnly.resolvedRef = {
    key: `branch/${baseBranch.id}`,
    value: baseBranch,
    spaceid: tx.serverOnly.spaceID,
    lastmodified: new Date(baseBranch.updatedAt || baseBranch.createdAt),
    version: 1,
  }

  await advanceRootBlockPostCommit(tx, {
    rootBlockId: baseBranch.workingRootBlockId,
    iso: newCommitTreeManager.root.value.createdAt,
    updatedBranchLineage: (newCommitTreeManager.root.value as RootBlock)
      .branchLineage,
  })

  perfReporter.checkpoint('advanced root block')

  // get it fresh from the cache
  const sourceRootBlock = await blockBaseOps
    .get(tx, baseBranch.workingRootBlockId)
    .then((b) => {
      invariant(
        b,
        `source root block ${baseBranch.workingRootBlockId} not found`
      )
      invariant(
        isRootBlock(b),
        `source root block ${baseBranch.workingRootBlockId} is not a root block`
      )
      return b
    })

  const deletedBlocks = new Set<string>()
  const upserted: BlockWithBR[] = []

  const now = new Date().toISOString()

  const sourceTreeManager = await tx.getTreeManager(sourceRootBlock.id)
  const allSourceBRsMap = await tx.getBrsByRootId(sourceRootBlock)

  const allSourceBlockIdsToProcess = new Set<string>()
  for (const node of sourceTreeManager.getAllNodes()) {
    allSourceBlockIdsToProcess.add(idToSourceBlockId(node.value))
  }
  for (const node of newCommitTreeManager.getAllNodes()) {
    allSourceBlockIdsToProcess.add(idToSourceBlockId(node.value))
  }

  await pMap(allSourceBlockIdsToProcess, async (sourceBlockId) => {
    const sourceBr = allSourceBRsMap.get(sourceBlockId)
    const sourceBlock = sourceTreeManager.getNode(sourceBlockId)?.value
    const newHeadBlock = newCommitTreeManager.getNode(sourceBlockId)?.value
    const newHeadBr = newCommitBRsIndexedMap.get(sourceBlockId)
    const commonAncestorBlock =
      ancestorCommitTreeManager?.getNode(sourceBlockId)?.value ?? null
    const commonAncestorBr = ancestorBRsIndexedMap?.get(sourceBlockId) ?? null

    const debug = false

    if (sourceBlock && isRootBlock(sourceBlock)) return

    if (debug) {
      console.log({
        sourceBlock,
        sourceBr,
        newHeadBlock,
        newHeadBr,
        commonAncestorBlock,
        commonAncestorBr,
      })
    }

    const mergeResult = mergeBlocks({
      debug,
      idSeed: getId(),
      now,
      sourceRootBlock,
      source:
        sourceBlock && sourceBr
          ? { block: cloneDeep(sourceBlock), br: cloneDeep(sourceBr) }
          : null,
      head:
        newHeadBlock && newHeadBr
          ? { block: cloneDeep(newHeadBlock), br: cloneDeep(newHeadBr) }
          : null,
      commonAncestor:
        commonAncestorBlock && commonAncestorBr
          ? {
              block: cloneDeep(commonAncestorBlock),
              br: cloneDeep(commonAncestorBr),
            }
          : null,
      resolveConflict: () => {
        return 'source'
      },
    })

    if (debug) {
      console.log('MERGE RESULT ————————————————')
      console.log(JSON.stringify(mergeResult, null, 2))
      console.log('mergeResult.value?.block', mergeResult.value?.block)
      console.log('mergeResult.value?.br', mergeResult.value?.br)
      console.log('sourceBr', sourceBr)
      console.log('headBr', newHeadBr)
    }

    if (mergeResult.sourceChanged) {
      switch (mergeResult.result) {
        case 'delete':
          if (sourceBlock && sourceBr) {
            deletedBlocks.add(sourceBr.blockId)
          }
          break
        case 'add':
          if (mergeResult.value) {
            upserted.push(mergeResult.value)
          }
          break
        case 'merge':
          invariant(sourceBlock, `sourceBlock not found`)
          invariant(sourceBr, `sourceBr not found`)
          if (mergeResult.value) {
            upserted.push(mergeResult.value)
          }
          break
        case 'conflict':
          throw new Error('Conflicts should have been resolved')
      }
    }
  })

  perfReporter.checkpoint('merged blocks')

  await fastForward(tx, {
    upserted,
    rootBlockId: sourceTreeManager.root.value.id,
    deleted: Array.from(deletedBlocks),
    now,
    perfReporter,
  })

  perfReporter.checkpoint('fast forwarded working tree')

  const updatedCR = cloneDeep(contentRequest)
  updatedCR.conflictResolutions = {}

  await repoSpaceMutators.updateContentRequest(tx, updatedCR)

  let author: { id: string; username: string }
  if (tx.serverOnly?.session) {
    invariant(
      tx.serverOnly?.session.sessionClaims.username,
      'username should be defined'
    )
    author = {
      id: tx.serverOnly.session.userId,
      username: tx.serverOnly.session.sessionClaims.username,
    }
  } else {
    invariant(authorId, 'session or authorId should be defined')
    const match = Object.values(SYSTEM_AUTHORS).find(
      (author) => author.id === authorId
    )
    invariant(match, `author ${authorId} not found`)
    author = {
      id: match.id,
      username: match.id,
    }
  }

  const notificationToSend: GenericNotification | null =
    type === 'merge' && !commitOutput.branch.isDefault
      ? {
          type: 'repo-cr-resolution',
          baseBranchId: commitOutput.branch.id,
          baseBranchName: commitOutput.branch.name,
          branchId: sourceBranch.id,
          branchName: sourceBranch.name,
          branchDescription: sourceBranch.description,
          authorId: author.id,
          authorUsername: author.username,
          createdAt: commitOutput.commit.createdAt,
          href: `/${tx.serverOnly.team?.slug}/${tx.serverOnly.repo?.slug}/history/${commitOutput.branch.name}/commit:${commitOutput.commit.id}`,
          read: false,
          resolution: 'merged',
        }
      : null

  await blockBaseOps.update(tx, {
    id: sourceTreeManager.root.value.id,
    lastRevertedAt: new Date().toISOString(),
  })

  console.log('[mergeCommit mutator] Merge commit completed successfully', {
    type,
    commitId: commitOutput.commit.id,
    baseBranchId: baseBranch.id,
    sourceBranchId: sourceBranch.id,
    hasNotificationToSend: !!notificationToSend,
  })

  perfReporter.end(true)

  return {
    commitMutation: {
      input: commitInput as any,
      output: commitOutput,
    },
    notificationToSend,
    baseBranch: await branchBaseOps.get(tx, baseBranch.id).then((b) => {
      invariant(b, `baseBranch ${baseBranch.id} not found`)
      return b
    }),
  }
}

mergeCommit.rollbackOnError = true

// utils

const getWorkingId = (block: Block) => {
  return block.sourceBlockId || block.id
}

function checkBlocksMatchAllConstraints({
  treeNode,
  treeManager,
}: {
  sourceBlockIdsToProcess: Set<string>
  treeNode: TreeNode<Block>
  treeManager: TreeManager<
    Block,
    Pick<CommonBlocksCompoundIndexes, 'blocksByType'>
  >
}): {
  matches: boolean
  errors: CommitError[]
} {
  let matches = true
  const errors: CommitError[] = []

  function checkBlockConstraints(
    _node: TreeNode<Block>,
    path: string,
    options: {
      isParentHidden: boolean
      forceHidden?: boolean
      overrideKey?: string
    }
  ) {
    const node = { ..._node }
    const newPath = path + '/' + node.value.id

    // SAME IF STATEMENT AS IN checkPrimitiveConstraints. KEEP IN SYNC
    if (
      options.forceHidden !== true &&
      // hidden blocks don't check constraints, but their children do (as they can be referenced).
      node.value.hidden === false &&
      // children of hidden blocks that are not referenceable, don't check their constraints.
      (options.isParentHidden === false || isReferenceableBlock(node.value))
    ) {
      switch (node.value.type) {
        case 'text':
        case 'number':
        case 'date':
        case 'code-snippet':
        case 'icon': {
          // primitive checks
          const primitiveValidation = checkPrimitiveConstraints(node.value, {
            isParentHidden: options.isParentHidden,
            forceHidden: options.forceHidden,
            overrideKey: options.overrideKey,
          })
          if (primitiveValidation.matches === false) {
            matches = false
            errors.push(...primitiveValidation.errors)
          }
          if (node.value.value) {
            if (node.value.type === 'icon') {
              if (!SvgValidator.isValid(node.value.value)) {
                matches = false
                errors.push({
                  type: 'regexPattern',
                  blockId: getWorkingId(node.value),
                  _testKey: `${node.value.type}-regex`,
                  title: node.value.title || 'Untitled',
                })
              }
            }
          }
          break
        }
        case 'rich-text': {
          const json = stateToJson(node.value.value.state)

          if (node.value.constraints.isRequired && isEmptyJson(json)) {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(node.value),
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-isRequired`,
              title: node.value.title || 'Untitled',
            })
          }

          const customBlocks: InstanceBlock[] = []
          const allowedNodes = getAllowedFormatting(node.value.constraints).map(
            optionToNewOptions
          )

          const notAllowedNodesInRichText: Set<string> = new Set()

          recursivelyProcessTipTapNodes(json.content as any, (tipTapNode) => {
            const blockId = getIdFromBaseHubNode(tipTapNode)
            if (blockId) {
              const block = treeManager.getNode(blockId, {
                fallbackKey: 'sourceBlockId',
              })?.value
              if (!block || !isInstanceBlock(block)) return
              customBlocks.push(block)
            } else if (allowedNodes) {
              // if allowedNodes is defined, we need to check if the node is allowed
              const nodeTypes = tiptapNodeToRichTextOption(tipTapNode)
              const notAllowedNodes = nodeTypes.filter(
                (node) =>
                  richTextOptions.includes(node) && !allowedNodes.includes(node)
              )
              if (notAllowedNodes.length) {
                notAllowedNodes.forEach((node) =>
                  notAllowedNodesInRichText.add(node)
                )
              }
            }
          })

          if (notAllowedNodesInRichText.size) {
            matches = false
            errors.push({
              type: 'allowedNodes',
              blockId: getWorkingId(node.value),
              unallowedNodesFound: Array.from(notAllowedNodesInRichText),
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-allowedNodes`,
              title: node.value.title || 'Untitled',
            })
          }

          const allowedComponents =
            node.value.constraints.allowedComponents || []
          if (
            customBlocks.some((customBlock) => {
              const componentReferencing = treeManager.getNode(
                customBlock.typeOptions.targetBlockId,
                { required: true, fallbackKey: 'sourceBlockId' }
              ).value

              return (
                !allowedComponents.includes(componentReferencing.id) &&
                !allowedComponents.includes(
                  componentReferencing.sourceBlockId ?? ''
                )
              )
            })
          ) {
            matches = false
            errors.push({
              type: 'allowedComponents',
              blockId: getWorkingId(node.value),
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-allowedComponents`,
              title: node.value.title || 'Untitled',
            })
          }

          // check components are all resolved in the treeManager
          allowedComponents.forEach((id) => {
            const childNode = treeManager.getNode(id, {
              fallbackKey: 'sourceBlockId',
            })
            if (!childNode || !isComponentBlock(childNode.value)) {
              matches = false
              errors.push({
                type: 'unresolvedComponent',
                blockId: getWorkingId(node.value),
                unresolvedComponentId: id,
                overrideKey: options.overrideKey,
                _testKey: `${node.value.type}-allowedComponents-unresolvedComponent`,
                title: node.value.title || 'Untitled',
              })
            }
          })
          break
        }
        case 'media': {
          // primitive checks
          const primitiveValidation = checkPrimitiveConstraints(node.value, {
            isParentHidden: options.isParentHidden,
            forceHidden: options.forceHidden,
            overrideKey: options.overrideKey,
          })
          if (primitiveValidation.matches === false) {
            matches = false
            errors.push(...primitiveValidation.errors)
          }
          break
        }
        case 'reference': {
          // primitive checks
          const primitiveValidation = checkPrimitiveConstraints(node.value, {
            isParentHidden: options.isParentHidden,
            forceHidden: options.forceHidden,
            overrideKey: options.overrideKey,
          })
          if (primitiveValidation.matches === false) {
            matches = false
            errors.push(...primitiveValidation.errors)
          }

          // tree health checks
          if (node.value.value.targetBlockIds.length) {
            let hasAtLeastOneNonHiddenTarget = false
            node.value.value.targetBlockIds.forEach((targetBlockId) => {
              const targetNode = treeManager.getNode(targetBlockId, {
                fallbackKey: 'sourceBlockId',
              })
              if (!targetNode || !isReferenceableBlock(targetNode.value)) {
                matches = false
                errors.push({
                  type: 'unresolvedComponent',
                  blockId: getWorkingId(node.value),
                  unresolvedComponentId: targetBlockId,
                  _testKey: `${node.value.type}-target-unresolvedComponent`,
                  title: node.value.title || 'Untitled',
                })
                return
              }
              const acceptedTypeOption = findAcceptedTypeOptionForBlock({
                refBlock: node.value as ReferenceBlock,
                evaluatedBlock: targetNode.value,
                typeOptions: getAcceptedTypeOptions({
                  refBlock: node.value as ReferenceBlock,
                  blockTreeManager: treeManager,
                }),
              })
              if (!acceptedTypeOption) {
                matches = false
                errors.push({
                  type: 'acceptedReferenceTypes',
                  blockId: getWorkingId(node.value),
                  _testKey: `${node.value.type}-target-acceptedReferenceTypes`,
                  title: node.value.title || 'Untitled',
                })
              }
              if (!targetNode.value.hidden) {
                hasAtLeastOneNonHiddenTarget = true
              }
            })
            if (
              !hasAtLeastOneNonHiddenTarget &&
              node.value.constraints.isRequired
            ) {
              matches = false
              errors.push({
                type: 'isRequired',
                blockId: getWorkingId(node.value),
                _testKey: `${node.value.type}-target-isRequired`,
                title: node.value.title || 'Untitled',
              })
            }
          }
          const children = (
            treeManager.getChildrenKeys(node.value.id) ?? []
          ).map((key) => treeManager.getNode(key, { required: true }).value)
          if (children.length) {
            children.forEach((child) => {
              const targetNode = treeManager.getNode(child.id, {
                required: true,
              })
              if (
                !isComponentBlock(targetNode.value) &&
                !isInstanceBlock(targetNode.value)
              ) {
                matches = false
                errors.push({
                  type: 'unresolvedComponent',
                  blockId: getWorkingId(node.value),
                  unresolvedComponentId: getWorkingId(targetNode.value),
                  overrideKey: options.overrideKey,
                  _testKey: `${node.value.type}-children-type-unresolvedComponent`,
                  title: node.value.title || 'Untitled',
                })
                return
              }
              invariant(targetNode, `target node ${child.id} not found`)
              invariant(
                isReferenceBlock(node.value),
                'expected reference block'
              )
              const acceptedTypeOption = findAcceptedTypeOptionForBlock({
                refBlock: node.value,
                evaluatedBlock: targetNode.value,
                typeOptions: getAcceptedTypeOptions({
                  refBlock: node.value,
                  blockTreeManager: treeManager,
                }),
              })
              if (!acceptedTypeOption) {
                if (!isComponentBlock(targetNode.value)) {
                  console.log(targetNode.value)
                  matches = false
                  errors.push({
                    type: 'acceptedUnionTypes',
                    blockId: getWorkingId(node.value),
                    overrideKey: options.overrideKey,
                    _testKey: `${node.value.type}-children-acceptedUnionTypes`,
                    title: node.value.title || 'Untitled',
                  })
                } else {
                  // for now, ignore the error if we're dealing with a component block (internal comp that was unlinked)
                }
              }
            })
          }

          // check components are all resolved in the treeManager
          node.value.constraints.acceptedTypes?.forEach((id) => {
            const childNode = treeManager.getNode(id, {
              fallbackKey: 'sourceBlockId',
            })
            if (!childNode || !isComponentBlock(childNode.value)) {
              matches = false
              errors.push({
                type: 'unresolvedComponent',
                blockId: getWorkingId(node.value),
                unresolvedComponentId: id,
                _testKey: `${node.value.type}-acceptedUnionTypes-unresolvedComponent`,
                title: node.value.title || 'Untitled',
              })
            }
          })
          break
        }
        case 'select': {
          // primitive checks
          const primitiveValidation = checkPrimitiveConstraints(node.value, {
            isParentHidden: options.isParentHidden,
            forceHidden: options.forceHidden,
            overrideKey: options.overrideKey,
          })
          if (primitiveValidation.matches === false) {
            matches = false
            errors.push(...primitiveValidation.errors)
          }
          break
        }
        case 'list': {
          // make sure target block is resolved
          const target = treeManager.getNode(
            node.value.typeOptions.targetBlockId,
            { fallbackKey: 'sourceBlockId' }
          )
          if (!target || !isComponentBlock(target.value)) {
            matches = false
            errors.push({
              type: 'unresolvedComponent',
              blockId: getWorkingId(node.value),
              unresolvedComponentId: node.value.typeOptions.targetBlockId,
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-target-unresolvedComponent`,
              title: node.value.title || 'Untitled',
            })
            break
          }

          // we get non-hidden children, and also check their targets are correctly resolved
          const children = (
            treeManager.getChildrenKeys(node.value.id) ?? []
          ).map((key) => treeManager.getNode(key, { required: true }).value)
          const nonHiddenChildrenList = children.filter((child) => {
            invariant(
              isListBlock(node.value),
              'expected node to be of type list'
            )
            const childNode = treeManager.getNode(child.id, {
              required: true,
            })
            if (
              !isInstanceBlock(childNode.value) &&
              !isComponentBlock(childNode.value)
            ) {
              return
            }

            if (isInstanceBlock(childNode.value)) {
              if (
                childNode.value.typeOptions.targetBlockId !==
                node.value.typeOptions.targetBlockId
              ) {
                matches = false
                errors.push({
                  type: 'unresolvedComponent',
                  blockId: getWorkingId(node.value),
                  unresolvedComponentId: node.value.typeOptions.targetBlockId,
                  overrideKey: options.overrideKey,
                  _testKey: `${node.value.type}-children-target-unresolvedComponent`,
                  title: node.value.title || 'Untitled',
                })
              }
            }

            if (isComponentBlock(childNode.value)) {
              if (
                childNode.value.id !== node.value.typeOptions.targetBlockId &&
                childNode.value.sourceBlockId !==
                  node.value.typeOptions.targetBlockId
              ) {
                matches = false
                errors.push({
                  type: 'unresolvedComponent',
                  blockId: getWorkingId(node.value),
                  unresolvedComponentId: node.value.typeOptions.targetBlockId,
                  overrideKey: options.overrideKey,
                  _testKey: `${node.value.type}-comp-children-unresolvedComponent`,
                  title: node.value.title || 'Untitled',
                })
              }
            }

            return !childNode.value.hidden
          })

          if (
            node.value.constraints.minRows &&
            node.value.constraints.minRows > nonHiddenChildrenList.length
          ) {
            matches = false
            errors.push({
              type: 'minRows',
              blockId: getWorkingId(node.value),
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-minRows`,
              title: node.value.title || 'Untitled',
            })
          }
          if (
            node.value.constraints.maxRows &&
            node.value.constraints.maxRows < nonHiddenChildrenList.length
          ) {
            matches = false
            errors.push({
              type: 'maxRows',
              blockId: getWorkingId(node.value),
              overrideKey: options.overrideKey,
              _testKey: `${node.value.type}-maxRows`,
              title: node.value.title || 'Untitled',
            })
          }
          break
        }
        case 'instance': {
          if (options.overrideKey) break
          // make sure target block is resolved
          const target = treeManager.getNode(
            node.value.typeOptions.targetBlockId,
            { fallbackKey: 'sourceBlockId' }
          )
          if (!target || !isComponentBlock(target.value)) {
            matches = false
            errors.push({
              type: 'unresolvedComponent',
              blockId: getWorkingId(node.value),
              unresolvedComponentId: node.value.typeOptions.targetBlockId,
              _testKey: `${node.value.type}-target-unresolvedComponent`,
              title: node.value.title || 'Untitled',
            })
            break
          }

          break
        }
        case 'variant-set': {
          const defaultVariants = node.value.value.variants.filter(
            (v) => v.isDefault
          )
          if (defaultVariants.length > 1) {
            errors.push({
              type: 'multipleDefaultVariants',
              blockId: getWorkingId(node.value),
              _testKey: `${node.value.type}-multipleDefaultVariants`,
              title: node.value.title || 'Untitled',
            })
          } else if (!defaultVariants[0]) {
            errors.push({
              type: 'missingDefaultVariant',
              blockId: getWorkingId(node.value),
              _testKey: `${node.value.type}-missingDefaultVariant`,
              title: node.value.title || 'Untitled',
            })
          } else {
            const defaultVariant = defaultVariants[0]
            if (
              defaultVariant.color !== 'var(--presence-raw-default)' ||
              defaultVariant.hidden !== false
            ) {
              errors.push({
                type: 'illegalVariantValue',
                blockId: getWorkingId(node.value),
                variantId: defaultVariant.id,
                invalidKey: defaultVariant.hidden ? 'hidden' : 'color',
                _testKey: `${node.value.type}-illegalVariantValue`,
                title: node.value.title || 'Untitled',
              })
            }
          }
          break
        }
        default:
          // no validation for other blocks?
          break
      }
    }

    let children = (treeManager.getChildrenKeys(node.key) ?? []).map(
      (key) => treeManager.getNode(key, { required: true }).value
    )

    // We don't check children when checking variant overrides, since they were already checked for the default case
    if (!options.overrideKey) {
      if (isRichTextBlock(node.value)) {
        // backwards compat to when we didn't remove deleted instances from rich text children
        const json = stateToJson(node.value.value.state)
        const instancesInRichText = getInstancesInRichText(
          node.value.value.state,
          json
        )

        children =
          children?.filter((c) => {
            if (instancesInRichText.includes(c.id)) return true
            const childNode = treeManager.getNode(c.id, { required: true })
            return instancesInRichText.includes(
              idToSourceBlockId(childNode.value)
            )
          }) ?? []
      }

      children?.forEach((child) => {
        if (child.id === node.key) {
          console.log({ child, node })
          throw new Error('child.id === node.key?')
        }
        const childNode = treeManager.getNode(child.id, { required: true })
        const childOptions = {
          isParentHidden: node.value.hidden || options.forceHidden === true,
          forceHidden:
            // rich text blocks forward their parent's hidden state to their children
            isRichTextBlock(node.value) && options.isParentHidden,
        }

        if (hasVariantOverrides(childNode.value)) {
          $overridesLoop: for (const [overrideKey, override] of Object.entries(
            childNode.value.variantOverrides ?? {}
          )) {
            const variantData = getVariantsDataFromKey(overrideKey)
            for (const data of variantData) {
              const set = treeManager.getNode(data.setId)?.value
              if (!set || !isVariantSetBlock(set) || set.hidden) {
                continue $overridesLoop
              }
              const variant = set.value.variants.find(
                (v) => v.id === data.variantId
              )
              if (variant?.hidden) continue $overridesLoop
            }

            checkBlockConstraints(
              {
                ...childNode,
                value: {
                  ...childNode.value,
                  value:
                    isRichTextBlock(childNode.value) ||
                    isReferenceBlock(childNode.value)
                      ? {
                          ...childNode.value.value,
                          ...override.value,
                        }
                      : override.value,
                },
              },
              newPath,
              { ...childOptions, overrideKey }
            )
          }
        }

        checkBlockConstraints(childNode, newPath, childOptions)
      })
    }
  }

  checkBlockConstraints(treeNode, '', { isParentHidden: false })

  return { matches, errors }
}

function checkPrimitiveConstraints(
  block: Block,
  options: {
    isParentHidden: boolean
    overrideKey?: string
    forceHidden?: boolean
  }
): {
  matches: boolean
  errors: CommitError[]
} {
  let matches = true
  const errors: CommitError[] = []

  // SAME IF STATEMENT AS IN checkBlocksMatchAllConstraints. KEEP IN SYNC
  if (
    options.forceHidden !== true &&
    // hidden blocks don't check constraints, but their children do (as they can be referenced).
    block.hidden === false &&
    // children of hidden blocks that are not referenceable, don't check their constraints.
    (options.isParentHidden === false || isReferenceableBlock(block))
  ) {
    const { overrideKey } = options

    switch (block.type) {
      case 'text': {
        if (block.constraints.isRequired) {
          if (block.value === null || block.value === '') {
            matches = false
            const workingId = getWorkingId(block)
            if (
              errors.some(
                (e) => e.type === 'isRequired' && e.blockId === workingId
              )
            ) {
              break
            }
            errors.push({
              type: 'isRequired',
              blockId: workingId,
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        const valueWithFallback = block.value ?? ''
        if (block.constraints.regexPattern) {
          const regex = new RegExp(block.constraints.regexPattern)
          if (valueWithFallback && !regex.test(valueWithFallback)) {
            matches = false
            errors.push({
              type: 'regexPattern',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-regexPattern`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (block.constraints.minLength && block.constraints.isRequired) {
          if (valueWithFallback.length < block.constraints.minLength) {
            matches = false
            errors.push({
              type: 'minLength',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-minLength`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (block.constraints.maxLength) {
          if (valueWithFallback.length > block.constraints.maxLength) {
            matches = false
            errors.push({
              type: 'maxLength',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-maxLength`,
              title: block.title || 'Untitled',
            })
          }
        }
        break
      }
      case 'number': {
        if (block.constraints.isRequired) {
          if (block.value === null) {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        const valueWithFallback = block.value ?? 0
        if (block.constraints.maxValue) {
          if (valueWithFallback > block.constraints.maxValue) {
            matches = false
            errors.push({
              type: 'maxValue',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-maxValue`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (block.constraints.minValue) {
          if (valueWithFallback < block.constraints.minValue) {
            matches = false
            errors.push({
              type: 'minValue',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-minValue`,
              title: block.title || 'Untitled',
            })
          }
        }
        break
      }
      case 'icon':
      case 'date': {
        if (block.constraints.isRequired) {
          if (block.value === null || block.value === '') {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        break
      }
      case 'code-snippet': {
        if (block.constraints.allowedLanguages !== null) {
          if (
            !block.constraints.allowedLanguages.includes(block.value.language)
          ) {
            matches = false
            errors.push({
              type: 'allowedLanguages',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-allowedLanguages`,
              title: block.title || 'Untitled',
            })
          }
        }
        break
      }
      case 'media':
        if (block.constraints.isRequired) {
          if (block.value.status !== 'uploaded') {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (block.constraints.maxSize) {
          const value = block.constraints.maxSize.split('-')[0] as string
          const unit = block.constraints.maxSize.split('-')[1] as string

          const sizeBytes = convertToBytes(value, unit as MaxSizeUnit)

          if (
            block.value.status === 'uploaded' &&
            block.value.fileSize > sizeBytes
          ) {
            matches = false
            errors.push({
              type: 'maxSize',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-maxSize`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (block.constraints.allowedMimeTypes) {
          if (block.value.status === 'uploaded') {
            if (
              !allowsAnyFile(block.constraints.allowedMimeTypes) &&
              !getAllowedMimeTypesArray(
                block.constraints.allowedMimeTypes
              ).includes(block.value.mimeType)
            ) {
              matches = false
              errors.push({
                type: 'allowedMimeTypes',
                blockId: getWorkingId(block),
                overrideKey,
                _testKey: `${block.type}-allowedMimeTypes`,
                title: block.title || 'Untitled',
              })
            }
          }
        }
        break
      case 'reference': {
        if (block.constraints.isRequired) {
          if (block.value.targetBlockIds.length < 1) {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        if (
          !block.constraints.multiple &&
          block.value.targetBlockIds.length > 1
        ) {
          matches = false
          errors.push({
            type: 'multipleValues',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-multipleValues`,
            title: block.title || 'Untitled',
          })
        }
        if (
          block.constraints.max !== null &&
          block.constraints.max !== undefined &&
          block.constraints.max < block.value.targetBlockIds.length
        ) {
          matches = false
          errors.push({
            type: 'allowedMaxReferences',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-allowedMaxReferences`,
            title: block.title || 'Untitled',
          })
        }
        if (
          block.constraints.min !== null &&
          block.constraints.min !== undefined &&
          block.constraints.min > block.value.targetBlockIds.length
        ) {
          matches = false
          errors.push({
            type: 'allowedMinReferences',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-allowedMinReferences`,
            title: block.title || 'Untitled',
          })
        }
        if (
          block.constraints.multiple !== null &&
          !block.constraints.multiple &&
          block.value.targetBlockIds.length > 1
        ) {
          matches = false
          errors.push({
            type: 'multipleValues',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-multipleValues`,
            title: block.title || 'Untitled',
          })
        }
        break
      }
      case 'select':
        if (block.constraints.isRequired) {
          if (block.value.length < 1) {
            matches = false
            errors.push({
              type: 'isRequired',
              blockId: getWorkingId(block),
              overrideKey,
              _testKey: `${block.type}-isRequired`,
              title: block.title || 'Untitled',
            })
          }
        }
        // not impl for now (should we ever? breaking change...)
        // if (block.constraints.max < block.value.length) {
        //   matches = false
        //   errors.push({
        //     type: 'maxValue',
        //     blockId: getWorkingId(block),
        //     overrideKey,
        //   })
        // }
        if (!block.constraints.multiple && block.value.length > 1) {
          matches = false
          errors.push({
            type: 'multipleValues',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-multipleValues`,
            title: block.title || 'Untitled',
          })
        }
        if (
          block.value.find(
            (value) =>
              !(block as SelectBlock).constraints.acceptedValues.includes(value)
          )
        ) {
          matches = false
          errors.push({
            type: 'acceptedValues',
            blockId: getWorkingId(block),
            overrideKey,
            _testKey: `${block.type}-acceptedValues`,
            title: block.title || 'Untitled',
          })
        }
        break
      default:
        // rest of the validation is considered "non-primitive"
        break
    }
  }

  return { matches, errors }
}

function getStagedBlockForCommit({
  rawStagedBlock,
  rawStagedBr,
  sourceBlockId,
  newBlockId,
  newRootBlockId,
  isoNow,
  branchId,
  updatedBranchLineage,
  playgroundId,
}: {
  rootBlockId: string
  rawStagedBlock: Block
  rawStagedBr: BlockRelationship
  sourceBlockId: string
  newBlockId: string
  newRootBlockId: string
  isoNow: string
  branchId: string
  updatedBranchLineage: BranchLineage | undefined
  playgroundId?: string
}): { block: Block; br: BlockRelationship } {
  let blockToCreate: Block | null = null
  let brToCreate: BlockRelationship | null = null
  switch (rawStagedBlock.type) {
    case 'rich-text': {
      const json = stateToJson(rawStagedBlock.value.state)
      const variantOverrides = rawStagedBlock.variantOverrides
      if (rawStagedBlock.variantOverrides) {
        for (const [oKey, override] of Object.entries(
          rawStagedBlock.variantOverrides
        )) {
          rawStagedBlock.variantOverrides[oKey] = {
            ...override,
            value: {
              ...override.value,
              state: JSON.stringify(stateToJson(override.value.state)),
            },
          }
        }
      }

      blockToCreate = {
        ...rawStagedBlock,
        variantOverrides,
        value: {
          ...rawStagedBlock.value,
          state: JSON.stringify(json),
        },
      }
      break
    }
    case 'comment': {
      blockToCreate = {
        ...rawStagedBlock,
        value: {
          ...rawStagedBlock.value,
          state: JSON.stringify(stateToJson(rawStagedBlock.value.state)),
        },
      }
      break
    }
    case 'root': {
      blockToCreate = {
        ...rawStagedBlock,
        createdAt: isoNow,
        updatedAt: isoNow,
        branchId,
        branchLineage: updatedBranchLineage,
      }
      if (!updatedBranchLineage) {
        delete blockToCreate.branchLineage
      }
      break
    }
    default:
      // just copy the rawStagedBlock
      blockToCreate = { ...rawStagedBlock }
      break
  }

  blockToCreate = {
    ...blockToCreate,
    id: newBlockId,
    isCommitted: true,
    sourceBlockId,
    createdAt: rawStagedBlock.createdAt ?? isoNow,
    updatedAt: rawStagedBlock.updatedAt ?? isoNow,
    ...(playgroundId ? { playgroundId } : {}),
  }

  if (blockToCreate.type === 'root') {
    blockToCreate.createdAt = isoNow
    blockToCreate.updatedAt = isoNow
  }

  brToCreate = {
    ...rawStagedBr,
    isCommitted: true,
    blockId: newBlockId,
    id: getBRId(newBlockId, newRootBlockId),
    branchId,
    validFrom: isoNow,
    ...(playgroundId ? { playgroundId } : {}),
  }
  delete brToCreate.validTo

  return { block: blockToCreate, br: brToCreate }
}

async function advanceRootBlockPostCommit(
  tx: WriteTransaction,
  {
    iso,
    rootBlockId,
    updatedBranchLineage,
  }: {
    iso: string
    rootBlockId: string
    updatedBranchLineage?: BranchLineage | undefined
  }
) {
  ensureBaseHubTransaction(tx)
  const rootBlock = await blockBaseOps.get(tx, rootBlockId)
  invariant(rootBlock, `root block ${rootBlockId} not found`)
  invariant(
    isRootBlock(rootBlock),
    `root block ${rootBlockId} is not a root block`
  )
  if (rootBlock.isCommitted) return
  await Promise.all([
    blockBaseOps.update(tx, {
      id: rootBlockId,
      createdAt: iso,
      updatedAt: iso,
      ...(updatedBranchLineage ? { branchLineage: updatedBranchLineage } : {}),
    }),
    blockRelationshipBaseOps.update(tx, {
      id: getBRId(rootBlockId, rootBlockId),
      validFrom: iso,
      branchId: rootBlock.branchId,
    }),
  ])
}

// exports
export const commitMutators = {
  getCommit: commitBaseOps.get,
  listCommits: commitBaseOps.list,
  commit,
  mergeCommit,
  discardWorkingChanges,
  hardContentReset,
}
